<template>
  <BaseFormFieldLayout
    :id="id"
    :note="note"
    :error-message="errorMessage"
    :field-class="fieldClass"
  >
    <div class="d-flex justify-content-between">
      <label v-if="!!label" class="form-label" @click="labelClickHandler">{{ label }}</label>
      <PlaceholderWrapper
        v-if="withPlaceholders"
        :placeholder-context="placeholderContext"
        :disabled="disabled"
        @placeholder-selected="onPlaceholderSelect"
      />
    </div>
    <div class="editor-wrapper" :class="{ 'is-invalid': errorMessage }">
      <div :id="`${id}-textEditor`"></div>
    </div>
  </BaseFormFieldLayout>
</template>

<script lang="ts" setup>
import { PropType, computed, onBeforeUnmount, onMounted, watch } from 'vue'
import DOMPurify from 'dompurify'
import Quill from 'quill'
import { asyncComponentWrapper } from '@/composables/useReloadModal'
import { PlaceholderContextsEnum } from '@/enums/PlaceholderEnum'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

const PlaceholderWrapper = asyncComponentWrapper(
  () => import('@/components/Misc/Placeholder/PlaceholderWrapper.vue'),
)

const props = defineProps({
  id: {
    type: [String || Number],
    required: true,
  },
  label: {
    type: String,
    default: '',
  },
  note: {
    type: String,
    default: '',
  },
  errorMessage: {
    type: String,
    default: '',
  },
  fieldClass: {
    type: String,
    default: '',
  },
  modelValue: {
    type: String,
    default: '',
  },
  withPlaceholders: {
    type: Boolean,
    default: false,
  },
  placeholderContext: {
    type: String as PropType<PlaceholderContextsEnum>,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

let quill: Quill | null = null // not using ref because https://github.com/slab/quill/issues/4375#issuecomment-2297454539

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const content = computed(() => {
  if (!props.modelValue) {
    return ''
  }

  let formattedP = props.modelValue.replace(/\n\n/g, '</p><p>')
  let formattedN = formattedP.replace(/\n/g, '<br>')
  let formattedL = formattedN.replace(/  +/g, (match) => '&nbsp;'.repeat(match.length))

  return formattedL
})

const onPlaceholderSelect = (placeholderValue: string) => {
  if (!quill) return

  const lastCursorPosition = quill.getSelection(true).index
  quill.insertText(lastCursorPosition, placeholderValue)
  quill.setSelection(lastCursorPosition + placeholderValue.length)
}

const labelClickHandler = () => {
  if (!quill) return
  quill.focus()
}

const TextChangeHandler = (_delta: unknown, _oldDelta: unknown, source: string) => {
  if (source !== Quill.sources.USER) return
  if (!quill) return

  const userInput = quill.root.innerHTML
  const safeUserInput = DOMPurify.sanitize(userInput, { ADD_ATTR: ['target'] })
  emit('update:modelValue', safeUserInput)
}

const setEditorContent = (value: string) => {
  if (!quill) return
  quill.clipboard.dangerouslyPasteHTML(value, Quill.sources.SILENT)
}

const setupQuill = (): Quill => {
  const Parchment = Quill.import('parchment') as unknown as any

  const customEnterBlotName = 'ShiftEnter'
  class ShiftEnterBlot extends Parchment.EmbedBlot {
    static blotName: string = customEnterBlotName
    static tagName: string = 'br'
  }

  Quill.register(ShiftEnterBlot, true)

  const bindings = {
    Enter: {
      key: 'Enter',
      shiftKey: true,
      handler: (range: { index: number; length: number }) => {
        if (!quill) return false

        quill.deleteText(range.index, range.length, Quill.sources.USER)
        quill.insertEmbed(range.index, customEnterBlotName, true, Quill.sources.USER)

        const [blot] = quill.getLeaf(range.index + 1) ?? []
        if (!blot?.next) {
          quill.insertEmbed(range.index + 1, customEnterBlotName, true, Quill.sources.USER)
        }

        quill.setSelection(range.index + 1, 0, Quill.sources.SILENT)
        return false
      },
    },
  }

  const quill = new Quill(`#${props.id}-textEditor`, {
    theme: 'snow',
    modules: {
      keyboard: {
        bindings: bindings,
      },
    },
  })

  setEditorContent(content.value)
  quill.on(Quill.events.TEXT_CHANGE, TextChangeHandler)

  return quill
}

const destroyQuill = (quill: Quill | null) => {
  if (quill) {
    quill.off(Quill.events.TEXT_CHANGE, TextChangeHandler)
    quill = null
  }
}

watch(
  () => content.value,
  (formattedValue) => {
    if (formattedValue) {
      setEditorContent(formattedValue)
    }
  },
)

watch(
  () => props.disabled,
  (isDisabled) => {
    if (!quill) return

    if (isDisabled) {
      quill.disable()
    } else {
      quill.enable()
    }
  },
  { immediate: true },
)

onMounted(() => {
  quill = setupQuill()
})

onBeforeUnmount(() => {
  destroyQuill(quill)
})
</script>

<style lang="scss">
.editor-wrapper {
  &.is-invalid {
    .ql-container.ql-snow,
    .ql-toolbar.ql-snow {
      border-color: var(--bs-red);
    }
  }

  .ql-container {
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    min-height: 7rem;
    font-family: 'Nunito', sans-serif !important;

    .ql-editor {
      min-height: 7rem;

      p {
        margin-bottom: 1rem;
      }
    }
  }

  .ql-toolbar {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
  }

  .ql-snow.ql-toolbar button.ql-active,
  .ql-snow .ql-toolbar button.ql-active,
  .ql-snow.ql-toolbar .ql-picker-label.ql-active,
  .ql-snow .ql-toolbar .ql-picker-label.ql-active,
  .ql-snow.ql-toolbar .ql-picker-item.ql-selected,
  .ql-snow .ql-toolbar .ql-picker-item.ql-selected {
    background-color: #fff;
    color: var(--bs-secondary);
  }

  .ql-picker-label > svg {
    color: var(--bs-secondary);
  }
}
</style>
